import { createInterfaceId, Injectable } from '@gitlab/needle';
import type { z } from 'zod';
import { Logger, withPrefix } from '@gitlab-org/logging';
import { doNotAwait } from '@gitlab-org/core';
import { ThemeSettingSchema, UserPersistentStorage } from '@gitlab-org/persistent-storage';
import {
  createThemeController,
  isThemeSetting,
  THEME_SETTINGS,
  type ThemeController,
  type ThemeSetting,
} from '@gitlab-org/tui';

type PersistedThemeSetting = z.infer<typeof ThemeSettingSchema>['name'];

THEME_SETTINGS satisfies readonly PersistedThemeSetting[];
ThemeSettingSchema.shape.name.options satisfies readonly ThemeSetting[];

/**
 * Owns the theme preference for the CLI session. Bridges the non-React world
 * (settings handler, initialization) and the TUI: the `controller` is handed
 * to the TUI's ThemeProvider, so `setSetting` restyles a running interface.
 *
 * Precedence: `GITLAB_DUO_CLI_THEME` env var > persisted user setting > `auto`
 * (follow the terminal appearance detected at startup).
 */
export interface ThemeService {
  /** Shared with the TUI's ThemeProvider to drive live theme switching. */
  readonly controller: ThemeController;
  /** The committed setting — live previews do not change it. */
  getSetting(): ThemeSetting;
  /** Applies a theme to the running TUI and persists it as the user preference. */
  setSetting(setting: ThemeSetting): void;
  /**
   * Applies a theme to the running TUI without committing it, e.g. while the
   * user moves through the options in settings. Preview with the committed
   * setting (`getSetting()`) to undo.
   */
  previewSetting(setting: ThemeSetting): void;
  /** True when GITLAB_DUO_CLI_THEME forces the theme; the settings UI disables the selector. */
  isEnvOverridden(): boolean;
  /** Applies the persisted preference; no-op while the env var override is active. */
  applyPersistedSetting(): Promise<void>;
}

export const ThemeService = createInterfaceId<ThemeService>('ThemeService');

/** `auto` is not an override — it is already the default behavior. */
export const getEnvThemeOverride = (): ThemeSetting | undefined => {
  const value = process.env.GITLAB_DUO_CLI_THEME;
  return isThemeSetting(value) && value !== 'auto' ? value : undefined;
};

@Injectable(ThemeService, [UserPersistentStorage, Logger])
export class DefaultThemeService implements ThemeService {
  readonly controller: ThemeController;

  #userPersistentStorage: UserPersistentStorage;

  #logger: Logger;

  #envOverride: ThemeSetting | undefined;

  /** The committed setting; the controller may temporarily diverge during previews. */
  #setting: ThemeSetting;

  constructor(userPersistentStorage: UserPersistentStorage, logger: Logger) {
    this.#userPersistentStorage = userPersistentStorage;
    this.#logger = withPrefix(logger, '[ThemeService]');
    this.#envOverride = getEnvThemeOverride();
    this.#setting = this.#envOverride ?? 'auto';
    this.controller = createThemeController(this.#setting);
  }

  getSetting(): ThemeSetting {
    return this.#setting;
  }

  isEnvOverridden(): boolean {
    return this.#envOverride !== undefined;
  }

  setSetting(setting: ThemeSetting): void {
    this.#setting = setting;
    this.controller.setSetting(setting);
    doNotAwait(this.#persist(setting));
  }

  previewSetting(setting: ThemeSetting): void {
    this.controller.setSetting(setting);
  }

  async applyPersistedSetting(): Promise<void> {
    if (this.#envOverride) {
      this.#logger.debug(`Theme forced to "${this.#envOverride}" by GITLAB_DUO_CLI_THEME`);
      return;
    }

    try {
      const stored = await this.#userPersistentStorage.get('theme');
      if (stored) {
        this.#logger.debug(`Applying persisted theme setting: ${stored.name}`);
        this.#setting = stored.name;
        this.controller.setSetting(stored.name);
      }
    } catch (error) {
      this.#logger.warn('Failed to load persisted theme setting', error);
    }
  }

  async #persist(setting: ThemeSetting): Promise<void> {
    try {
      await this.#userPersistentStorage.set('theme', { name: setting });
    } catch (error) {
      this.#logger.error('Failed to persist theme setting', error);
    }
  }
}
